const DEFAULT_SCIP_SAVE_DELAY = 1000
const DEFAULT_PROPERTY_SAVE_DELAY = 0
const DEFAULT_QUESTION_SAVE_DELAY = 1500
const DEFAULT_INSTRUCTION_SAVE_DELAY = 1000

const getService = (context, name) => {
    const service = context.services && context.services[name]

    if (typeof service !== 'function') {
        throw new Error(`Compliance Information requires services.${name}()`)
    }

    return service
}

const replaceArray = (target, values) => {
    target.splice(0, target.length, ...(values || []))
}

const hasListQuestionState = (context, list, state) => {
    const questionStates = context.product && Array.isArray(context.product.substanceListQuestionStates)
        ? context.product.substanceListQuestionStates
        : []
    const currentState = questionStates.find(item => String(item.list) === String(list.id))

    return currentState && currentState.state === state
}

const createComplianceInformationController = ({
    context,
    setDirty,
    setSaving,
    publishEffect,
    reportError,
    scipSaveDelay = DEFAULT_SCIP_SAVE_DELAY,
    propertySaveDelay = DEFAULT_PROPERTY_SAVE_DELAY,
    questionSaveDelay = DEFAULT_QUESTION_SAVE_DELAY,
    refreshSubstancesAfterPropertySave = true,
    refreshSubstanceStateAfterSave = true,
}) => {
    let validation = { isValid: false, errors: [] }
    let substanceRevision = 0
    let savedSubstanceRevision = 0
    let cardRevision = 0
    let savedCardRevision = 0
    let pendingOperation = Promise.resolve()
    let scipSaveTimeout = null
    let propertySaveTimeout = null
    let componentPropertySaveTimeout = null
    let questionSaveTimeout = null
    let instructionSaveTimeout = null
    let instructionRevision = 0
    let savedInstructionRevision = 0
    const pendingPropertyActions = []
    const pendingComponentPropertyActions = []
    const pendingComponentScipChanges = {}
    const componentInstructionChanges = {}
    let disposed = false

    const publishCommitted = (type, scopes) => publishEffect({ type, phase: 'committed', scopes })
    const hasSubstanceChanges = () => savedSubstanceRevision < substanceRevision
    const hasCardChanges = () => savedCardRevision < cardRevision
    const hasComponentInstructionChanges = () => Object.values(componentInstructionChanges)
        .some(change => change.savedRevision < change.revision)
    const syncDirty = () => setDirty(
        hasSubstanceChanges()
        || hasCardChanges()
        || savedInstructionRevision < instructionRevision
        || hasComponentInstructionChanges()
        || pendingComponentPropertyActions.length > 0
        || Object.keys(pendingComponentScipChanges).length > 0,
    )

    const queueSaving = operation => {
        pendingOperation = pendingOperation
            .catch(() => undefined)
            .then(async () => {
                setSaving(true)
                try {
                    const result = await operation()
                    reportError(null)
                    return result
                } catch (error) {
                    reportError(error)
                    throw error
                } finally {
                    setSaving(false)
                    syncDirty()
                }
            })

        return pendingOperation
    }

    const refreshSubstances = async () => {
        const { included = [], excluded = [] } = await getService(context, 'refreshConcernSubstances')()
        replaceArray(context.includedSubstances, included)
        replaceArray(context.excludedSubstances, excluded)
        if (refreshSubstanceStateAfterSave) {
            await getService(context, 'refreshSubstanceState')()
        }
    }

    const refreshComponents = async () => {
        const components = await getService(context, 'getComponentConcernSubstances')()
        replaceArray(context.componentConcernSubstances, components)
    }

    const refreshAfterDocumentProcessing = async () => {
        await getService(context, 'refreshComplianceProductCard')()
        await Promise.all([
            refreshSubstances(),
            refreshComponents(),
        ])
        await getService(context, 'refreshProductWeightRequirement')().catch(() => false)
        context.complianceInformation.appliedDocumentProcessingRevision = Number(
            context.documentsAndWebpages.completedProcessingRevision,
        ) || 0
    }

    const saveSubstances = async ({ refresh = true } = {}) => {
        if (!hasSubstanceChanges()) {
            return
        }

        const revision = substanceRevision
        await getService(context, 'saveSubstances')()
        savedSubstanceRevision = Math.max(savedSubstanceRevision, revision)
        if (refresh && !hasSubstanceChanges()) {
            await refreshSubstances()
        }
        await publishCommitted('product.concern-substances.changed', ['product.concern-substances'])
    }

    const saveCard = async () => {
        if (!hasCardChanges()) {
            return
        }

        const revision = cardRevision
        await getService(context, 'saveProductCard')()
        savedCardRevision = Math.max(savedCardRevision, revision)
        await publishCommitted('product.card.changed', ['product.card'])
    }

    const markDirty = channel => {
        if (channel === 'substances') {
            substanceRevision += 1
        } else {
            cardRevision += 1
        }
        syncDirty()
    }

    const flushChanges = async (refreshSubstancesAfterSave = true) => {
        await saveSubstances({ refresh: refreshSubstancesAfterSave })
        await saveCard()
    }

    const savePendingChanges = () => queueSaving(flushChanges)

    const saveInstructions = async () => {
        if (savedInstructionRevision >= instructionRevision) {
            return
        }

        const revision = instructionRevision
        await getService(context, 'saveProductSafetyInstructions')()
        savedInstructionRevision = Math.max(savedInstructionRevision, revision)
        if (instructionRevision > revision) {
            const instructions = context.complianceInformation.safetyInstructions
            getService(context, 'setProductSafeUseApplicable')(
                instructions.isSafeUseApplicable,
            )
            getService(context, 'setProductSafeUseInstructions')(
                instructions.safeUseDescription,
            )
            getService(context, 'setProductDisassemblingInstructions')(
                instructions.disassemblingDescription,
            )
        }
        await publishCommitted(
            'product.verification-documents.changed',
            ['product.verification-documents'],
        )
    }

    const scheduleInstructionSave = () => {
        instructionRevision += 1
        syncDirty()
        clearTimeout(instructionSaveTimeout)
        instructionSaveTimeout = setTimeout(() => {
            instructionSaveTimeout = null
            queueSaving(saveInstructions).catch(() => undefined)
        }, DEFAULT_INSTRUCTION_SAVE_DELAY)
    }

    const saveComponentInstructions = async componentId => {
        const change = componentInstructionChanges[String(componentId)]
        if (!change || change.savedRevision >= change.revision) {
            return
        }

        const revision = change.revision
        await getService(context, 'saveComplianceComponentSafetyInstructions')(componentId)
        const latestInstructions = {
            ...getService(context, 'getComplianceComponentSafetyInstructions')(componentId),
        }
        await refreshComponents()
        change.savedRevision = Math.max(change.savedRevision, revision)
        if (change.revision > revision) {
            getService(context, 'updateComplianceComponentSafetyInstructions')(
                componentId,
                latestInstructions,
            )
        }
        await publishCommitted(
            'product.verification-documents.changed',
            ['product.verification-documents'],
        )
    }

    const scheduleComponentInstructionSave = componentId => {
        const key = String(componentId)
        const change = componentInstructionChanges[key] || {
            componentId,
            revision: 0,
            savedRevision: 0,
            timeout: null,
        }
        componentInstructionChanges[key] = change
        change.revision += 1
        syncDirty()
        clearTimeout(change.timeout)
        change.timeout = setTimeout(() => {
            change.timeout = null
            queueSaving(() => saveComponentInstructions(componentId)).catch(() => undefined)
        }, DEFAULT_INSTRUCTION_SAVE_DELAY)
    }

    const flushComponentInstructions = async () => {
        const componentIds = Object.keys(componentInstructionChanges)
        for (const componentId of componentIds) {
            await saveComponentInstructions(componentInstructionChanges[componentId].componentId)
        }
    }

    const runActionSave = async (operation, payloads) => {
        const actions = Array.isArray(payloads) ? payloads : [payloads]
        actions.forEach(payload => {
            if (typeof payload.savingCallback === 'function') {
                payload.savingCallback()
            }
        })

        try {
            const result = await operation()
            actions.forEach(payload => {
                if (typeof payload.completionCallback === 'function') {
                    payload.completionCallback()
                }
            })
            return result
        } catch (error) {
            actions.forEach(payload => {
                if (typeof payload.failureCallback === 'function') {
                    payload.failureCallback(error)
                }
            })
            throw error
        }
    }

    const takePendingPropertyActions = () => pendingPropertyActions.splice(
        0,
        pendingPropertyActions.length,
    )

    const flushPropertyChanges = async () => {
        clearTimeout(propertySaveTimeout)
        propertySaveTimeout = null
        const actions = takePendingPropertyActions()
        if (actions.length === 0) {
            return
        }

        await runActionSave(
            () => queueSaving(() => flushChanges(refreshSubstancesAfterPropertySave)),
            actions,
        )
    }

    const schedulePropertySave = payload => {
        pendingPropertyActions.push(payload)
        clearTimeout(propertySaveTimeout)
        if (propertySaveDelay <= 0) {
            propertySaveTimeout = null
            return flushPropertyChanges()
        }

        propertySaveTimeout = setTimeout(() => {
            propertySaveTimeout = null
            flushPropertyChanges().catch(() => undefined)
        }, propertySaveDelay)
        return Promise.resolve()
    }

    const flushComponentPropertyChanges = async () => {
        clearTimeout(componentPropertySaveTimeout)
        componentPropertySaveTimeout = null
        const actions = pendingComponentPropertyActions.splice(
            0,
            pendingComponentPropertyActions.length,
        )
        if (actions.length === 0) {
            return
        }
        const componentIds = Array.from(new Set(actions.map(action => action.componentId)))

        await runActionSave(
            () => queueSaving(() => Promise.all(componentIds.map(componentId => (
                getService(context, 'saveComplianceComponentSubstances')(componentId)
            )))),
            actions,
        )
        await publishCommitted(
            'product.component-concern-substances.changed',
            ['product.component-concern-substances'],
        )
    }

    const scheduleComponentPropertySave = payload => {
        pendingComponentPropertyActions.push(payload)
        syncDirty()
        clearTimeout(componentPropertySaveTimeout)
        if (propertySaveDelay <= 0) {
            componentPropertySaveTimeout = null
            return flushComponentPropertyChanges()
        }

        componentPropertySaveTimeout = setTimeout(() => {
            componentPropertySaveTimeout = null
            flushComponentPropertyChanges().catch(() => undefined)
        }, propertySaveDelay)
        return Promise.resolve()
    }

    const applySubstanceMutation = async (mutation, payload, save) => {
        if (!save) {
            mutation()
            markDirty('substances')
            return
        }

        clearTimeout(propertySaveTimeout)
        propertySaveTimeout = null
        const actions = [...takePendingPropertyActions(), payload]
        await runActionSave(
            () => queueSaving(async () => {
                mutation()
                markDirty('substances')
                await flushChanges()
            }),
            actions,
        )
    }

    const scheduleScipSave = value => {
        getService(context, 'changeScipNumber')(value)
        markDirty('card')
        clearTimeout(scipSaveTimeout)
        scipSaveTimeout = setTimeout(() => {
            scipSaveTimeout = null
            savePendingChanges().catch(() => undefined)
        }, scipSaveDelay)
    }

    const flushComponentScipChanges = async componentId => {
        const keys = typeof componentId === 'undefined'
            ? Object.keys(pendingComponentScipChanges)
            : [String(componentId)]

        await Promise.all(keys.map(async key => {
            const change = pendingComponentScipChanges[key]
            if (!change) {
                return
            }
            clearTimeout(change.timeout)
            delete pendingComponentScipChanges[key]

            await runActionSave(
                () => queueSaving(() => getService(
                    context,
                    'saveComplianceComponentScipNumber',
                )(change.componentId, change.value)),
                change.actions,
            )
        }))
        syncDirty()
    }

    const scheduleComponentScipSave = payload => {
        const key = String(payload.componentId)
        const current = pendingComponentScipChanges[key] || {
            componentId: payload.componentId,
            actions: [],
            timeout: null,
            value: '',
        }
        clearTimeout(current.timeout)
        current.value = payload.value || ''
        current.actions.push(payload)
        current.timeout = setTimeout(() => {
            current.timeout = null
            flushComponentScipChanges(payload.componentId).catch(() => undefined)
        }, scipSaveDelay)
        pendingComponentScipChanges[key] = current
        getService(context, 'setComplianceComponentScipNumber')(
            payload.componentId,
            current.value,
        )
        syncDirty()
    }

    const scheduleQuestionSave = () => {
        clearTimeout(questionSaveTimeout)
        questionSaveTimeout = setTimeout(() => {
            questionSaveTimeout = null
            savePendingChanges().catch(() => undefined)
        }, questionSaveDelay)
    }

    return Object.freeze({
        async load() {
            await refreshComponents()
            await getService(context, 'refreshProductWeightRequirement')().catch(() => false)
        },
        setValidation(nextValidation) {
            validation = nextValidation
        },
        validate() {
            return Promise.resolve(validation)
        },
        refreshAfterDocumentProcessing,
        async handleAction(action) {
            if (disposed) {
                return
            }

            const payload = action.payload || {}
            switch (action.type) {
            case 'forward':
                await getService(context, 'forwardRequest')()
                return
            case 'substance.include':
                getService(context, 'includeConcernSubstance')(payload.substance, payload.substanceListIds)
                markDirty('substances')
                return
            case 'substance.exclude':
                await applySubstanceMutation(
                    () => getService(context, 'excludeConcernSubstance')(payload.substance),
                    payload,
                    payload.save,
                )
                return
            case 'substances.exclude':
                if (!Array.isArray(payload.substances) || payload.substances.length === 0) {
                    if (typeof payload.completionCallback === 'function') {
                        payload.completionCallback()
                    }
                    return
                }
                await applySubstanceMutation(
                    () => payload.substances.forEach(substance => {
                        getService(context, 'excludeConcernSubstance')(substance)
                    }),
                    payload,
                    payload.save,
                )
                return
            case 'substance.undefined':
                await applySubstanceMutation(
                    () => getService(context, 'makeConcernSubstanceUndefined')(payload.substance),
                    payload,
                    payload.save,
                )
                return
            case 'substance-properties.change':
                getService(context, 'updateConcernSubstanceProperties')(
                    payload.substance,
                    payload.properties,
                )
                markDirty('substances')
                await schedulePropertySave(payload)
                return
            case 'information-owner.change':
                await queueSaving(() => getService(context, 'changeInformationOwner')(payload.informationOwner))
                await publishCommitted('request.details.changed', ['request.details'])
                return
            case 'has-concern-substances.change':
                getService(context, 'setHasConcernSubstances')(payload.value)
                markDirty('card')
                await savePendingChanges()
                return
            case 'list-question.update':
                if (hasListQuestionState(context, payload.list, payload.state)) {
                    return
                }
                getService(context, 'updateListQuestionState')(payload.list, payload.state)
                markDirty('card')
                scheduleQuestionSave()
                return
            case 'characteristics.change':
                getService(context, 'setProductCharacteristics')(payload.characteristics)
                markDirty('card')
                await savePendingChanges()
                await getService(context, 'refreshProductWeightRequirement')().catch(() => false)
                return
            case 'selector.closed':
                await savePendingChanges()
                await getService(context, 'refreshProductWeightRequirement')().catch(() => false)
                return
            case 'components.changed':
                await queueSaving(async () => {
                    await refreshComponents()
                    await getService(context, 'refreshProductWeightRequirement')().catch(() => false)
                })
                await publishCommitted(
                    'product.component-concern-substances.changed',
                    ['product.component-concern-substances'],
                )
                return
            case 'component-substance.saved':
                await getService(context, 'refreshProductWeightRequirement')().catch(() => false)
                return
            case 'component-selector.open':
                try {
                    const data = await getService(context, 'getComplianceComponentEditorData')(
                        payload.componentId,
                    )
                    reportError(null)
                    if (typeof payload.successCallback === 'function') {
                        payload.successCallback(data)
                    }
                } catch (error) {
                    reportError(error)
                    if (typeof payload.failureCallback === 'function') {
                        payload.failureCallback(error)
                    }
                    throw error
                }
                return
            case 'component-substance.include':
                getService(context, 'includeComplianceComponentSubstance')(
                    payload.componentId,
                    payload.substance,
                    payload.substanceListIds,
                    payload.draft === true,
                )
                return
            case 'component-substance.exclude':
                if (payload.draft === true) {
                    getService(context, 'excludeComplianceComponentSubstance')(
                        payload.componentId,
                        payload.substance,
                        true,
                    )
                    return
                }
                await flushComponentPropertyChanges()
                await queueSaving(() => runActionSave(async () => {
                    getService(context, 'excludeComplianceComponentSubstance')(
                        payload.componentId,
                        payload.substance,
                        false,
                    )
                    if (payload.save) {
                        await getService(context, 'saveComplianceComponentSubstances')(
                            payload.componentId,
                        )
                        await refreshComponents()
                    }
                }, payload))
                return
            case 'component-substance.undefined':
                getService(context, 'makeComplianceComponentSubstanceUndefined')(
                    payload.componentId,
                    payload.substance,
                    payload.draft === true,
                )
                return
            case 'component-list-question.update':
                await runActionSave(() => queueSaving(async () => {
                    await getService(context, 'updateComplianceComponentListQuestion')(
                        payload.componentId,
                        payload.list,
                        payload.state,
                    )
                    Array.from(payload.substances || []).forEach(substance => {
                        getService(context, 'excludeComplianceComponentSubstance')(
                            payload.componentId,
                            substance,
                        )
                    })
                    if (payload.substances && payload.substances.length > 0) {
                        await getService(context, 'saveComplianceComponentSubstances')(
                            payload.componentId,
                        )
                        await refreshComponents()
                    }
                }), payload)
                return
            case 'component-selector.closed':
                await flushComponentPropertyChanges()
                await runActionSave(() => queueSaving(async () => {
                    await getService(context, 'saveComplianceComponentSubstances')(
                        payload.componentId,
                        payload.draft === true,
                    )
                    await refreshComponents()
                    await getService(context, 'refreshProductWeightRequirement')().catch(() => false)
                }), payload)
                await publishCommitted(
                    'product.component-concern-substances.changed',
                    ['product.component-concern-substances'],
                )
                return
            case 'component-substance-properties.change':
                payload.container.properties = payload.properties
                await getService(context, 'updateComplianceComponentSubstanceProperties')(
                    payload.componentId,
                    payload.substance,
                    payload.properties,
                )
                await scheduleComponentPropertySave(payload)
                return
            case 'scip-number.change':
                scheduleScipSave(payload.value || '')
                return
            case 'component-scip-number.change':
                scheduleComponentScipSave(payload)
                return
            case 'safe-use-applicability.change':
                context.complianceInformation.safetyInstructions.isSafeUseApplicable = payload.value
                getService(context, 'setProductSafeUseApplicable')(payload.value)
                scheduleInstructionSave()
                return
            case 'safe-use-instructions.change':
                context.complianceInformation.safetyInstructions.safeUseDescription = payload.value || ''
                getService(context, 'setProductSafeUseInstructions')(payload.value || '')
                scheduleInstructionSave()
                return
            case 'disassembling-instructions.change':
                context.complianceInformation.safetyInstructions.disassemblingDescription = payload.value || ''
                getService(context, 'setProductDisassemblingInstructions')(payload.value || '')
                scheduleInstructionSave()
                return
            case 'component-safe-use-applicability.change':
                getService(context, 'updateComplianceComponentSafetyInstructions')(
                    payload.componentId,
                    { isSafeUseApplicable: payload.value },
                )
                scheduleComponentInstructionSave(payload.componentId)
                return
            case 'component-safe-use-instructions.change':
                getService(context, 'updateComplianceComponentSafetyInstructions')(
                    payload.componentId,
                    { safeUseDescription: payload.value || '' },
                )
                scheduleComponentInstructionSave(payload.componentId)
                return
            case 'component-disassembling-instructions.change':
                getService(context, 'updateComplianceComponentSafetyInstructions')(
                    payload.componentId,
                    { disassemblingDescription: payload.value || '' },
                )
                scheduleComponentInstructionSave(payload.componentId)
                return
            default:
                throw new Error(`Unsupported Compliance Information action "${action.type}"`)
            }
        },
        async flush() {
            clearTimeout(scipSaveTimeout)
            scipSaveTimeout = null
            clearTimeout(propertySaveTimeout)
            propertySaveTimeout = null
            clearTimeout(componentPropertySaveTimeout)
            componentPropertySaveTimeout = null
            clearTimeout(questionSaveTimeout)
            questionSaveTimeout = null
            clearTimeout(instructionSaveTimeout)
            instructionSaveTimeout = null
            Object.values(pendingComponentScipChanges).forEach(change => {
                clearTimeout(change.timeout)
                change.timeout = null
            })
            Object.values(componentInstructionChanges).forEach(change => {
                clearTimeout(change.timeout)
                change.timeout = null
            })
            await pendingOperation.catch(() => undefined)
            await flushPropertyChanges()
            await flushComponentPropertyChanges()
            await flushComponentScipChanges()
            await queueSaving(saveInstructions)
            await queueSaving(flushComponentInstructions)
            await savePendingChanges()
            await pendingOperation
        },
        async handleEffect(effect) {
            if (effect.type === 'request.reply-document-processing.completed') {
                await refreshAfterDocumentProcessing()
                return
            }

            if (['request.details.changed', 'product.card.changed'].includes(effect.type)) {
                await refreshComponents()
            }
        },
        dispose() {
            disposed = true
            clearTimeout(scipSaveTimeout)
            scipSaveTimeout = null
            clearTimeout(propertySaveTimeout)
            propertySaveTimeout = null
            clearTimeout(componentPropertySaveTimeout)
            componentPropertySaveTimeout = null
            clearTimeout(questionSaveTimeout)
            questionSaveTimeout = null
            clearTimeout(instructionSaveTimeout)
            instructionSaveTimeout = null
            Object.values(pendingComponentScipChanges).forEach(change => {
                clearTimeout(change.timeout)
                change.timeout = null
            })
            Object.values(componentInstructionChanges).forEach(change => {
                clearTimeout(change.timeout)
                change.timeout = null
            })
        },
    })
}

export { createComplianceInformationController }
export default createComplianceInformationController
